import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MatchStreamPlayer from './MatchStreamPlayer';
import MatchTabs from '../components/match/MatchTabs';
import apiService from '../services/api';
import './MatchDetailPage.css';

const MatchDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [matchInfo, setMatchInfo] = useState(null);
  const [matchStreams, setMatchStreams] = useState(null);
  // No longer need selectedChannel - auto-playing
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMatchDetails();
    // Silent auto-refresh every 20s for live updates
    const intervalId = setInterval(() => fetchMatchDetails(true), 20000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchMatchDetails = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);

      // Fetch specific match details using the new API endpoint
      const matchData = await apiService.getMatchDetails(id);

      if (matchData.success && matchData.match) {
        const match = matchData.match;
        if (match) {
          console.log('Match score object:', match.score);
          console.log('Home score:', match.score?.home, typeof match.score?.home);
          console.log('Away score:', match.score?.away, typeof match.score?.away);
          setMatchInfo(match);
          
          // Use match-specific channels with logos from API
          if (match.channelsWithLogos && Array.isArray(match.channelsWithLogos)) {
            const totalChannels = match.channelsWithLogos.length;

            // Convert enriched channel data to stream objects expected by MatchStreamPlayer
            const streamObjects = match.channelsWithLogos.map((channelData, index) => {
              // Use token-based streaming if token is available
              const streamUrl = channelData.streamToken
                ? `/api/stream/token/${channelData.streamToken}`
                : `/api/stream/${id}/${channelData.id || `stream-${index}`}`;

              return {
                id: channelData.id || `stream-${index}`,
                name: channelData.name,
                logo: channelData.logo,
                quality: channelData.qualities?.[0] || 'HD',
                qualities: channelData.qualities || ['HD'],
                streamUrl: streamUrl,
                url: streamUrl,
                provider: channelData.providers?.[0] || channelData.provider || 'IPTV',
                providers: channelData.providers || [channelData.provider || 'IPTV'],
                streamCount: channelData.streamCount || 1,
                language: channelData.name // Use channel name as the language/category
              };
            });

            // Create byLanguage structure where each channel is its own category
            const byLanguage = {};
            streamObjects.forEach(streamObj => {
              byLanguage[streamObj.name] = [streamObj]; // Each channel name gets its own category with one stream
            });

            console.log(`📺 Match has ${totalChannels} relevant channels:`, streamObjects);
            setMatchStreams({
              total: totalChannels,
              channels: streamObjects,
              byQuality: { 'HD': streamObjects },
              byLanguage: byLanguage // Each channel name is a separate "language" option
            });
          } else if (match.channels && Array.isArray(match.channels)) {
            // Handle new channel format: {id, name, logo} or fallback string format
            const totalChannels = match.channels.length;
            const streamObjects = match.channels.map((channel, index) => {
              // Check if channel is object or string
              const isObject = typeof channel === 'object' && channel !== null;
              const channelName = isObject ? channel.name : channel;
              const channelId = isObject ? channel.id : `stream-${index}`;
              const channelLogo = isObject ? channel.logo : null;

              return {
                id: channelId,
                name: channelName,
                logo: channelLogo,
                quality: 'HD',
                streamUrl: `/api/stream/${id}/${channelId}`,
                url: `/api/stream/${id}/${channelId}`,
                provider: 'IPTV',
                language: channelName
              };
            });

            const byLanguage = {};
            streamObjects.forEach(streamObj => {
              byLanguage[streamObj.name] = [streamObj];
            });

            console.log(`📺 Match has ${totalChannels} basic channels (no logos):`, streamObjects);
            setMatchStreams({
              total: totalChannels,
              channels: streamObjects,
              byQuality: { 'HD': streamObjects },
              byLanguage: byLanguage
            });
          } else {
            console.log('⚠️ No channels available for this match');
            setMatchStreams({
              total: 0,
              channels: [],
              byQuality: {},
              byLanguage: {}
            });
          }
        } else {
          setError('Match not found');
          return;
        }
      } else {
        setError('Failed to load match data');
        return;
      }

    } catch (err) {
      console.error('Error fetching match details:', err);
      setError('Failed to load match details');
    } finally {
      setLoading(false);
    }
  };

  // No longer need channel selection - auto-playing best quality

  if (loading) {
    return (
      <div className="match-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading match details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="match-detail-page">
        <div className="error-container">
          <h2>Error</h2>
          <p>{String(error)}</p>
          <button onClick={() => navigate('/')} className="back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (!matchInfo) {
    return (
      <div className="match-detail-page">
        <div className="error-container">
          <h2>Match Not Found</h2>
          <p>The requested match could not be found.</p>
          <button onClick={() => navigate('/')} className="back-btn">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="match-detail-page">
      <div className="page-container">
        {/* Match Header */}
        <div className="page-header">
          <div className="match-header">
            <div className="match-teams">
              <div className="team">
                <img src={String(matchInfo.teams?.home?.logo || matchInfo.homeTeamLogo || matchInfo.homeLogo || '')} alt={String(matchInfo.teams?.home?.name || matchInfo.homeTeam || '')} />
                <span>{String(matchInfo.teams?.home?.name || matchInfo.homeTeam || '')}</span>
              </div>
              <div className="match-score">
                {(matchInfo.status === 'SC' || matchInfo.status === 'scheduled') && matchInfo.date ? (
                  (() => {
                    try {
                      const date = new Date(matchInfo.date);
                      if (!isNaN(date.getTime())) {
                        const hours = date.getHours().toString().padStart(2, '0');
                        const minutes = date.getMinutes().toString().padStart(2, '0');
                        return <span>{hours}:{minutes}</span>;
                      }
                    } catch (e) {}
                    return (
                      <>
                        <span>0</span>
                        <span>-</span>
                        <span>0</span>
                      </>
                    );
                  })()
                ) : (
                  <>
                    <span>{String(matchInfo.score?.home ?? matchInfo.homeScore ?? 0)}</span>
                    <span>-</span>
                    <span>{String(matchInfo.score?.away ?? matchInfo.awayScore ?? 0)}</span>
                  </>
                )}
              </div>
              <div className="team">
                <img src={String(matchInfo.teams?.away?.logo || matchInfo.awayTeamLogo || matchInfo.awayLogo || '')} alt={String(matchInfo.teams?.away?.name || matchInfo.awayTeam || '')} />
                <span>{String(matchInfo.teams?.away?.name || matchInfo.awayTeam || '')}</span>
              </div>
            </div>
            <div className="match-status">
              <span className={`status ${matchInfo.status === 'live' ? 'live' : ''}`}>
                {matchInfo.status === 'live' ? 'LIVE' : String(matchInfo.status || '').toUpperCase()}
              </span>
              {matchInfo.minute && matchInfo.status !== 'finished' && (
                <span className="minute">{String(matchInfo.minute || '')}'</span>
              )}
            </div>
          </div>
        </div>

        {/* Match Stream Player */}
        {matchInfo && (
          <div className="player-section">
            <MatchStreamPlayer
              matchStreams={matchStreams}
              matchInfo={matchInfo}
            />
          </div>
        )}

        {/* Match Tabs (Events, Lineups, Stats, H2H, Standings) */}
        {matchInfo && <MatchTabs match={matchInfo} />}

        {/* Match Details */}
        {matchInfo && (
          <div className="match-details">
            <div className="match-info-card">
            <h2>Match Information</h2>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">League:</span>
                <span className="info-value">{matchInfo.league?.name || 'Unknown League'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Date:</span>
                <span className="info-value">
                  {(() => {
                    try {
                      return new Date(matchInfo.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      });
                    } catch (e) {
                      return String(matchInfo.date || 'TBD');
                    }
                  })()}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Time:</span>
                <span className="info-value">
                  {(() => {
                    try {
                      const timeStr = new Date(matchInfo.date).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      });
                      return timeStr;
                    } catch (e) {
                      return 'TBD';
                    }
                  })()}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Venue:</span>
                <span className="info-value">{String(matchInfo.venue || 'TBD')}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Status:</span>
                <span className={`info-value status-${matchInfo.status}`}>
                  {matchInfo.status === 'finished' ? 'FT' : 
                   matchInfo.status === 'scheduled' ? 'SC' :
                   matchInfo.status === 'live' ? `${String(matchInfo.minute || '')}'` :
                   String(matchInfo.status || '').toUpperCase()}
                </span>
              </div>
              {matchInfo.round && (
                <div className="info-item">
                  <span className="info-label">Round:</span>
                  <span className="info-value">{String(matchInfo.round || '')}</span>
                </div>
              )}
            </div>
          </div>

          {/* Stream Info */}
          {matchStreams && (
            <div className="stream-info-card">
              <h2>Available Languages</h2>
              <div className="language-summary">
                {Object.entries(matchStreams?.byLanguage || {}).map(([lang, streams]) => {
                  if (!streams || !Array.isArray(streams)) return null;
                  
                  const flagCode = lang === 'en' ? 'gb' :
                                  lang === 'ar' ? 'sa' :
                                  lang === 'es' ? 'es' :
                                  lang === 'fr' ? 'fr' :
                                  lang === 'de' ? 'de' :
                                  lang === 'it' ? 'it' :
                                  lang === 'po' ? 'pt' :
                                  lang === 'tr' ? 'tr' : 'un';
                  const langName = lang === 'en' ? 'English' :
                                  lang === 'ar' ? 'Arabic' :
                                  lang === 'es' ? 'Spanish' :
                                  lang === 'fr' ? 'French' :
                                  lang === 'de' ? 'German' :
                                  lang === 'it' ? 'Italian' :
                                  lang === 'po' ? 'Portuguese' :
                                  lang === 'tr' ? 'Turkish' : String(lang);
                  return (
                    <div key={String(lang)} className="language-item">
                      <img 
                        className="language-flag" 
                        src={`https://flagcdn.com/24x18/${String(flagCode)}.png`}
                        srcSet={`https://flagcdn.com/48x36/${String(flagCode)}.png 2x, https://flagcdn.com/72x54/${String(flagCode)}.png 3x`}
                        alt={`${String(lang)} flag`}
                        width="24"
                        height="18"
                      />
                      <span className="language-name">{String(langName)}</span>
                      <span className="stream-count">{streams.length} stream{streams.length !== 1 ? 's' : ''}</span>
                    </div>
                  );
                }).filter(Boolean)}
              </div>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default MatchDetailPage;